import { useLocale } from 'hooks/useLocale/useLocale';
import { HistoryList } from 'ui/historyList/HistoryList';
import { useLocaleStoreQuery } from 'hooks/useLocaleStoreQuery/useLocaleStoreQuery';
import { filterQueryFromPagination } from 'helpers/filterQueryFromPagination';
import { AppMessages } from 'i18n/messages';
import { Functionality } from 'api/userPermissions/userPermissions.enum';
import { UserPermissionsHistory as UserPermissionsHistoryType } from 'api/userPermissions/userPermissions.types';

import { UserPermissionsHistoryFilters } from './userPermissionsHistoryFilters/UserPermissionsHistoryFilters';
import { UserPermissionsHistoryProps } from './UserPermissionsHistory.types';

export const UserPermissionsHistory = ({
  historyResponse,
  isLoading,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  userSearchQuery,
  setUserSearchQuery,
  onClearHistoryQuery,
}: UserPermissionsHistoryProps) => {
  const { formatMessage } = useLocale();

  const {
    storedQueries: { userPermissionsHistory: userPermissionsHistoryQuery },
  } = useLocaleStoreQuery();

  const isClearFiltersButtonVisible =
    Object.values(filterQueryFromPagination(userPermissionsHistoryQuery)).some((query) => query) || !!userSearchQuery;

  const getFunctionalityLabel = (functionality?: Functionality) => {
    if (!functionality) return '';
    const functionalityLabels: Record<Functionality, string> = {
      [Functionality.URZADZENIA_GLC]: formatMessage({ id: AppMessages['permissions.functionality.devices'] }),
      [Functionality.HARMONOGRAMY]: formatMessage({ id: AppMessages['permissions.functionality.schedules'] }),
      [Functionality.KARTY_POSIADACZE]: formatMessage({ id: AppMessages['permissions.functionality.cards'] }),
      [Functionality.SEKWENCJA]: formatMessage({ id: AppMessages['permissions.functionality.sequences'] }),
      [Functionality.POSZUKIWANY]: formatMessage({ id: AppMessages['permissions.functionality.wanted'] }),
      [Functionality.KONTROLA]: formatMessage({ id: AppMessages['permissions.functionality.control'] }),
      [Functionality.ZDARZENIA]: formatMessage({ id: AppMessages['permissions.functionality.events'] }),
      [Functionality.ZDARZENIA_INNE]: formatMessage({ id: AppMessages['permissions.functionality.eventsOther'] }),
      [Functionality.UPR_GRUPOWE]: formatMessage({ id: AppMessages['permissions.functionality.groupPermissions'] }),
      [Functionality.UPR_INDYWIDUALNE]: formatMessage({
        id: AppMessages['permissions.functionality.individualPermissions'],
      }),
      [Functionality.KOMUNIKAT]: formatMessage({ id: AppMessages['permissions.functionality.announcements'] }),
      [Functionality.DEFINICJE]: formatMessage({ id: AppMessages['permissions.functionality.definitions'] }),
      [Functionality.UZYTKOWNICY]: formatMessage({ id: AppMessages['permissions.functionality.users'] }),
      [Functionality.HISTORIA]: formatMessage({ id: AppMessages['permissions.functionality.history'] }),
      [Functionality.ARCHIWUM]: formatMessage({ id: AppMessages['permissions.functionality.archive'] }),
      [Functionality.LICENCJA]: formatMessage({ id: AppMessages['permissions.functionality.license'] }),
      [Functionality.ZESTAWIENIE_UPRAWNIEN]: formatMessage({
        id: AppMessages['permissions.functionality.permissionsSummary'],
      }),
      [Functionality.RAPORT_WYDANYCH_KART]: formatMessage({
        id: AppMessages['permissions.functionality.issuedCardsReport'],
      }),
      [Functionality.RAPORT_POSIADACZY_BEZ_ZDARZEN]: formatMessage({
        id: AppMessages['permissions.functionality.ownersWithoutEventsReport'],
      }),
      [Functionality.RAPORT_ZDARZEN]: formatMessage({ id: AppMessages['permissions.functionality.eventsReport'] }),
      [Functionality.RAPORT_ZDARZEN_INNE]: formatMessage({
        id: AppMessages['permissions.functionality.eventsReportOther'],
      }),
      [Functionality.RAPORT_CZASU_OBECNOSCI]: formatMessage({
        id: AppMessages['permissions.functionality.timeOfOwnershipReport'],
      }),
      [Functionality.OBECNOSCI]: formatMessage({ id: AppMessages['permissions.functionality.presence'] }),
      [Functionality.RAPORT_OSOB_OBECNYCH]: formatMessage({
        id: AppMessages['permissions.functionality.presentPersonsReport'],
      }),
    };
    return functionalityLabels[functionality] || functionality;
  };

  const getBooleanLabel = (value?: boolean) => {
    if (value === undefined) return '';
    return value ? formatMessage({ id: AppMessages['common.yes'] }) : formatMessage({ id: AppMessages['common.no'] });
  };

  const getModifyActionLabel = (value?: boolean) => {
    if (value === undefined) return '';
    return value
      ? formatMessage({ id: AppMessages['permissions.action.edit'] })
      : formatMessage({ id: AppMessages['permissions.action.read'] });
  };

  return (
    <HistoryList
      data={historyResponse?.data || []}
      isLoading={isLoading}
      filters={{
        isClearButtonVisible: isClearFiltersButtonVisible,
        onClearFilters: onClearHistoryQuery,
        render: () => (
          <UserPermissionsHistoryFilters userSearchQuery={userSearchQuery} setUserSearchQuery={setUserSearchQuery} />
        ),
      }}
      pagination={{
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        count: historyResponse?.count || 0,
      }}
      renderHistoryItemBody={(history: UserPermissionsHistoryType) => (
        <>
          {history.wykonawca && (
            <HistoryList.ActionBodyItem
              title={formatMessage({ id: AppMessages['user.history.executor'] })}
              value={`${history.wykonawca.nazwisko} ${history.wykonawca.imie}`}
            />
          )}

          {history.functionalPermissions?.map((functionalPermission, index) => {
            const items = [];
            const { functionality, viewAction, modifyAction } = functionalPermission;

            if (viewAction && viewAction.old !== viewAction.new) {
              items.push({
                oldValue: getBooleanLabel(viewAction.old),
                newValue: getBooleanLabel(viewAction.new),
              });
            }

            if (modifyAction && modifyAction.old !== modifyAction.new) {
              items.push({
                oldValue: getModifyActionLabel(modifyAction.old),
                newValue: getModifyActionLabel(modifyAction.new),
              });
            }

            return items.length > 0 ? (
              <HistoryList.ActionModifiedGroupedFieldBodyItem
                key={`${functionality}-${index}`}
                title={getFunctionalityLabel(functionality)}
                items={items}
              />
            ) : null;
          })}

          {history.dataPermissions && (
            <HistoryList.ActionModifiedFieldBodyItem
              title={formatMessage({ id: AppMessages['user.permissions.history.dataAccess'] })}
              oldValue={history.dataPermissions.old || ''}
              newValue={history.dataPermissions.new || ''}
            />
          )}

          {history.accessAllData && (
            <HistoryList.ActionModifiedFieldBodyItem
              title={formatMessage({ id: AppMessages['user.permissions.history.accessAllData'] })}
              oldValue={getBooleanLabel(history.accessAllData.old)}
              newValue={getBooleanLabel(history.accessAllData.new)}
            />
          )}

          {history.accessFeaturedCards && (
            <HistoryList.ActionModifiedFieldBodyItem
              title={formatMessage({ id: AppMessages['user.permissions.history.accessFeaturedCards'] })}
              oldValue={getBooleanLabel(history.accessFeaturedCards.old)}
              newValue={getBooleanLabel(history.accessFeaturedCards.new)}
            />
          )}

          {history.accessNoOrganizations && (
            <HistoryList.ActionModifiedFieldBodyItem
              title={formatMessage({ id: AppMessages['user.permissions.history.accessNoOrganizations'] })}
              oldValue={getBooleanLabel(history.accessNoOrganizations.old)}
              newValue={getBooleanLabel(history.accessNoOrganizations.new)}
            />
          )}

          {history.powod && (
            <HistoryList.ActionBodyItem
              title={formatMessage({ id: AppMessages['user.history.reason'] })}
              value={history.powod}
            />
          )}
        </>
      )}
    />
  );
};
